import React, { useMemo, useState, useEffect } from "react";
import {
  BarChart3,
  Calendar,
  Download,
  Plus,
  Search,
  Ticket,
  X,
  Zap,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import B2CHeader from "./B2CHeader";
import { Badge, Card, FieldBase, PrimaryButton, SecondaryButton, SelectBase, cx } from "./b2cUi";

const CouponsManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [filteredFlashSales, setFilteredFlashSales] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("coupons");
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Mock data - replace with actual API call
  const mockCoupons = [
    {
      id: 1,
      code: "WELCOME10",
      name: "Welcome Discount",
      type: "percentage",
      value: 10,
      minOrder: 50,
      maxDiscount: 25,
      usageLimit: 1000,
      usedCount: 245,
      status: "active",
      startDate: "2024-01-01",
      endDate: "2024-12-31",
      applicableProducts: "all",
      customerGroups: ["new", "regular"]
    },
    {
      id: 2,
      code: "SAVE20",
      name: "Summer Sale",
      type: "fixed",
      value: 20,
      minOrder: 100,
      maxDiscount: null,
      usageLimit: 500,
      usedCount: 89,
      status: "active",
      startDate: "2024-06-01",
      endDate: "2024-08-31",
      applicableProducts: "electronics",
      customerGroups: ["all"]
    },
    {
      id: 3,
      code: "VIP15",
      name: "VIP Exclusive",
      type: "percentage",
      value: 15,
      minOrder: 200,
      maxDiscount: 50,
      usageLimit: 100,
      usedCount: 67,
      status: "active",
      startDate: "2024-01-01",
      endDate: "2024-12-31",
      applicableProducts: "premium",
      customerGroups: ["vip"]
    },
    {
      id: 4,
      code: "EXPIRED5",
      name: "Old Coupon",
      type: "percentage",
      value: 5,
      minOrder: 25,
      maxDiscount: 10,
      usageLimit: 200,
      usedCount: 200,
      status: "expired",
      startDate: "2023-01-01",
      endDate: "2023-12-31",
      applicableProducts: "all",
      customerGroups: ["all"]
    }
  ];

  const mockFlashSales = [
    {
      id: 1,
      name: "Black Friday Electronics",
      description: "Up to 70% off on electronics",
      discount: 70,
      startDate: "2024-11-29",
      endDate: "2024-11-30",
      startTime: "00:00",
      endTime: "23:59",
      status: "scheduled",
      products: ["PROD-001", "PROD-002", "PROD-003"],
      categories: ["electronics"],
      priority: 1
    },
    {
      id: 2,
      name: "Weekend Flash Sale",
      description: "50% off on selected items",
      discount: 50,
      startDate: "2024-01-20",
      endDate: "2024-01-21",
      startTime: "09:00",
      endTime: "18:00",
      status: "active",
      products: ["PROD-004", "PROD-005"],
      categories: ["home", "fashion"],
      priority: 2
    },
    {
      id: 3,
      name: "New Year Clearance",
      description: "Clearance sale on old inventory",
      discount: 80,
      startDate: "2024-01-01",
      endDate: "2024-01-07",
      startTime: "00:00",
      endTime: "23:59",
      status: "completed",
      products: ["PROD-006", "PROD-007", "PROD-008"],
      categories: ["clearance"],
      priority: 3
    }
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setCoupons(mockCoupons);
      setFlashSales(mockFlashSales);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = activeTab === "coupons" ? coupons : flashSales;

    // Filter by search term
    if (searchFilter) {
      if (activeTab === "coupons") {
        filtered = filtered.filter(coupon =>
          coupon.code.toLowerCase().includes(searchFilter.toLowerCase()) ||
          coupon.name.toLowerCase().includes(searchFilter.toLowerCase())
        );
      } else {
        filtered = filtered.filter(sale =>
          sale.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
          sale.description.toLowerCase().includes(searchFilter.toLowerCase())
        );
      }
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Filter by type (for coupons only)
    if (activeTab === "coupons" && typeFilter !== "all") {
      filtered = filtered.filter(coupon => coupon.type === typeFilter);
    }

    if (activeTab === "coupons") {
      setFilteredCoupons(filtered);
    } else {
      setFilteredFlashSales(filtered);
    }
  }, [coupons, flashSales, searchFilter, statusFilter, typeFilter, activeTab]);

  const handleCreateItem = () => {
    setShowCreateModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowCreateModal(true);
  };

  const handleDeleteItem = (itemId) => {
    const itemType = activeTab === "coupons" ? "coupon" : "flash sale";
    if (window.confirm(`Are you sure you want to delete this ${itemType}?`)) {
      if (activeTab === "coupons") {
        setCoupons(prev => prev.filter(coupon => coupon.id !== itemId));
      } else {
        setFlashSales(prev => prev.filter(sale => sale.id !== itemId));
      }
    }
  };

  const handleToggleStatus = (itemId) => {
    if (activeTab === "coupons") {
      setCoupons(prev =>
        prev.map(coupon =>
          coupon.id === itemId
            ? { ...coupon, status: coupon.status === 'active' ? 'inactive' : 'active' }
            : coupon
        )
      );
    } else {
      setFlashSales(prev =>
        prev.map(sale =>
          sale.id === itemId
            ? { ...sale, status: sale.status === 'active' ? 'inactive' : 'active' }
            : sale
        )
      );
    }
  };

  const statusTone = (status) => {
    const statusConfig = {
      active: "green",
      inactive: "slate",
      expired: "rose",
      scheduled: "blue",
      completed: "purple",
      draft: "yellow",
    };
    return statusConfig[status] || "slate";
  };

  const getUsagePercentage = (used, limit) => {
    return limit > 0 ? (used / limit) * 100 : 0;
  };

  const isExpiringSoon = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  };

  const activeCouponsCount = useMemo(
    () => coupons.filter((c) => c.status === "active").length,
    [coupons]
  );
  const activeFlashSalesCount = useMemo(
    () => flashSales.filter((s) => s.status === "active").length,
    [flashSales]
  );
  const totalCouponUsage = useMemo(() => coupons.reduce((sum, c) => sum + c.usedCount, 0), [coupons]);
  const totalFlashSalesRevenue = useMemo(
    () => flashSales.reduce((sum, s) => sum + s.discount * 100, 0),
    [flashSales]
  ); // mock calc

  const tabBase =
    "px-3 py-2 rounded-2xl text-sm font-black border transition-colors inline-flex items-center gap-2";
  const tabActive = "bg-white border-slate-200 text-slate-900";
  const tabInactive = "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100";

  return (
    <AdminPageShell
      title="Coupons & Flash Sales"
      description="Create and manage promotional campaigns and discount codes. (Mock data)"
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="blue">
            <Ticket className="h-4 w-4" />
            {activeCouponsCount} active coupons
          </Badge>
          <Badge tone="yellow">
            <Zap className="h-4 w-4" />
            {activeFlashSalesCount} active flash sales
          </Badge>
          <Badge tone="purple">
            <BarChart3 className="h-4 w-4" />
            {totalCouponUsage} total uses
          </Badge>
          <Badge tone="gray">${totalFlashSalesRevenue} revenue impact</Badge>
        </div>
      }
      actions={
        <>
          <SecondaryButton
            type="button"
            onClick={() => {
              setSearchFilter("");
              setStatusFilter("all");
              setTypeFilter("all");
            }}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleCreateItem}
          >
            <Plus className="h-4 w-4" />
            Create {activeTab === "coupons" ? "Coupon" : "Flash Sale"}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-6">
        <B2CHeader />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Ticket className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Active Coupons</p>
                <p className="text-xl font-black text-slate-900">{activeCouponsCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Zap className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Active Flash Sales</p>
                <p className="text-xl font-black text-slate-900">{activeFlashSalesCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Total Usage</p>
                <p className="text-xl font-black text-slate-900">{totalCouponUsage}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <span className="text-sm font-black text-slate-700">$</span>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Revenue Impact</p>
                <p className="text-xl font-black text-slate-900">${totalFlashSalesRevenue}</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("coupons")}
                className={cx(tabBase, activeTab === "coupons" ? tabActive : tabInactive)}
              >
                <Ticket className="h-4 w-4" />
                Coupons ({coupons.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("flashsales")}
                className={cx(tabBase, activeTab === "flashsales" ? tabActive : tabInactive)}
              >
                <Zap className="h-4 w-4" />
                Flash Sales ({flashSales.length})
              </button>
            </div>

            <SecondaryButton type="button" onClick={() => alert("Export coming soon")}
            >
              <Download className="h-4 w-4" />
              Export
            </SecondaryButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
            <div className="md:col-span-6">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={
                    activeTab === "coupons"
                      ? "Search coupons by code or name"
                      : "Search flash sales by name or description"
                  }
                  className={cx(FieldBase, "pl-10")}
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Status</label>
              <select className={SelectBase} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
                {activeTab === "flashsales" ? (
                  <>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                  </>
                ) : null}
              </select>
            </div>

            <div className="md:col-span-3">
              {activeTab === "coupons" ? (
                <>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Type</label>
                  <select className={SelectBase} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="all">All Types</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </>
              ) : (
                <div className="h-full" />
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
              <p className="mt-2 text-slate-600">Loading data…</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    {activeTab === "coupons" ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Coupon Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Discount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Usage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Validity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Actions
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Sale Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Discount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Schedule
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Products
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">
                          Actions
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                      {(activeTab === "coupons" ? filteredCoupons : filteredFlashSales).map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          {activeTab === "coupons" ? (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {item.code}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Min Order: ${item.minOrder}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-slate-900">
                                  {item.type === 'percentage' ? `${item.value}%` : `$${item.value}`}
                                </div>
                                {item.maxDiscount && (
                                  <div className="text-xs text-slate-600">
                                    Max: ${item.maxDiscount}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900">
                                  {item.usedCount} / {item.usageLimit}
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                                  <div
                                    className="bg-[#D2042D] h-2 rounded-full"
                                    style={{ width: `${getUsagePercentage(item.usedCount, item.usageLimit)}%` }}
                                  ></div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900">
                                  {new Date(item.startDate).toLocaleDateString()}
                                </div>
                                <div className={cx("text-sm", isExpiringSoon(item.endDate) ? "text-rose-700" : "text-slate-600")}>
                                  to {new Date(item.endDate).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge tone={statusTone(item.status)}>
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Badge>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-semibold text-slate-900">
                                    {item.name}
                                  </div>
                                  <div className="text-sm text-slate-600">
                                    {item.description}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Priority: {item.priority}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-slate-900">
                                  {item.discount}% OFF
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900 flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-slate-500" />
                                  {new Date(item.startDate).toLocaleDateString()}
                                </div>
                                <div className="text-sm text-slate-600">
                                  {item.startTime} - {item.endTime}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-slate-900">
                                  {item.products.length} products
                                </div>
                                <div className="text-xs text-slate-600">
                                  {item.categories.join(', ')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge tone={statusTone(item.status)}>
                                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                </Badge>
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex flex-wrap items-center gap-2">
                              <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => handleEditItem(item)}>
                                Edit
                              </SecondaryButton>
                              <SecondaryButton
                                type="button"
                                className={cx(
                                  "px-3 py-1.5 text-xs",
                                  item.status === "active"
                                    ? "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                )}
                                onClick={() => handleToggleStatus(item.id)}
                              >
                                {item.status === "active" ? "Deactivate" : "Activate"}
                              </SecondaryButton>
                              <SecondaryButton
                                type="button"
                                className="px-3 py-1.5 text-xs border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                Delete
                              </SecondaryButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
          )}

          {(activeTab === "coupons" ? filteredCoupons : filteredFlashSales).length === 0 && !loading ? (
            <div className="text-center py-8">
              <p className="text-slate-600">No {activeTab === "coupons" ? "coupons" : "flash sales"} found matching your criteria.</p>
              <PrimaryButton type="button" className="mt-4" onClick={handleCreateItem}
              >
                <Plus className="h-4 w-4" />
                Create Your First {activeTab === "coupons" ? "Coupon" : "Flash Sale"}
              </PrimaryButton>
            </div>
          ) : null}
        </Card>
      </div>

      {showCreateModal ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-slate-900">
                {editingItem ? `Edit ${activeTab === "coupons" ? "Coupon" : "Flash Sale"}` : `Create New ${activeTab === "coupons" ? "Coupon" : "Flash Sale"}`}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingItem(null);
                }}
                className="inline-flex items-center justify-center h-9 w-9 rounded-2xl border border-slate-200 hover:bg-slate-50"
              >
                <X className="h-4 w-4 text-slate-700" />
              </button>
            </div>
            
            <div className="space-y-4">
              {activeTab === "coupons" ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Coupon Code
                      </label>
                      <input
                        type="text"
                        className={FieldBase}
                        placeholder="e.g., WELCOME10"
                        defaultValue={editingItem?.code || ''}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Coupon Name
                      </label>
                      <input
                        type="text"
                        className={FieldBase}
                        placeholder="e.g., Welcome Discount"
                        defaultValue={editingItem?.name || ''}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Discount Type
                      </label>
                      <select className={SelectBase}>
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Discount Value
                      </label>
                      <input
                        type="number"
                        className={FieldBase}
                        placeholder="10"
                        defaultValue={editingItem?.value || ''}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                      Flash Sale Name
                    </label>
                    <input
                      type="text"
                      className={FieldBase}
                      placeholder="e.g., Black Friday Electronics"
                      defaultValue={editingItem?.name || ''}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                      Description
                    </label>
                    <textarea
                      className={cx(
                        "w-full min-h-[96px] resize-y rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                      )}
                      rows="3"
                      placeholder="Describe the flash sale"
                      defaultValue={editingItem?.description || ''}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Discount Percentage
                      </label>
                      <input
                        type="number"
                        className={FieldBase}
                        placeholder="50"
                        defaultValue={editingItem?.discount || ''}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                        Priority
                      </label>
                      <input
                        type="number"
                        className={FieldBase}
                        placeholder="1"
                        defaultValue={editingItem?.priority || ''}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <SecondaryButton
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingItem(null);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={() => alert("Save coming soon")}
              >
                {editingItem ? "Update" : "Create"} {activeTab === "coupons" ? "Coupon" : "Flash Sale"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
};

export default CouponsManagement;
